<template>
  <el-container
    class="module-container visit-container">
    <template v-if="Visit.list.hasError">
      <InformationFetchingError :errors="Visit.list.errors" />
    </template>

    <template v-else>
      <MobileFilterButton :visits-module="true" />
      <el-header height="auto" class="module-container__header">
        <VisitListHeader :on-filter="onFilter">
          <div slot="searches" class="searches search-web">
            <el-input v-model="searchQuery" :placeholder="$t('Search')">
              <el-button slot="append"
                         icon="mdi mdi-magnify"
                         @click="onFilter(searchQuery)" />
            </el-input>
          </div>
        </VisitListHeader>
      </el-header>

      <div class="searches search-mobile">
        <el-input v-model="searchQuery" :placeholder="$t('Search')">
          <el-button slot="append"
                     icon="mdi mdi-magnify"
                     @click="onFilter(searchQuery)" />
        </el-input>
      </div>

      <el-main class="module-container__main mobilePadding">
        <ScoreAverages :report-data="likeReportData"
                       type="visit_index"
                       :score-indicator="currentScoreIndicator"
                       class="visit-list-report-scores"
                       :is-loading="Visit.list.isLoading"
                       :highlighted-score="highlightedScore"
                       @highlightScore="highlightScore"
                       @disHighlightScore="highlightScore($event, false)"
                       @selectScoreCategory="selectScoreCategory" />
        <VisitList :loading="Visit.list.isLoading"
                   :list="list"
                   :fetch-params="fetchParams"
                   :meta="meta"
                   :score-indicator="currentScoreIndicator"
                   :on-filter="onFilter"
                   :default-sort="getDefaultSort"
                   :on-sort-change="onSortChange"
                   :highlighted-score="highlightedScore"
                   :selected-score-category="selectedScoreCategory"
                   show-store-fields
                   show-auditor-fields
                   @reportStatus="handleReportStatusChange"
                   @handleExport="onExport"
                   @highlightScore="highlightScore"
                   @disHighlightScore="highlightScore($event, false)"
                   @virtual-scroll="handleVirtualScroll"
                   @visitListIds="updateVisitListIds">
          <div class="visit-list-header">
            <VisitListStats :stats="stats" :total="meta.total" />
          </div>
        </VisitList>
      </el-main>
    </template>
  </el-container>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { result } from 'lodash';
import MobileFilterButton from '@/components/MobileFilterButton';
import ScoreAverages from '@/components/ScoreAverages';
import VisitListStats from '@/components/ListStats';
import mobileScreenControl from '@/helpers/mixins/mobileScreenControl';
import DrilldownNavigationHelper from '@/helpers/mixins/DrilldownNavigationHelper';
import BackendPaginationMixin from '../../helpers/mixins/backendPagination';
import WorkWithDashboardFilter from '../../helpers/mixins/workWithDashboardFilter';
import VisitListHeader from './components/ListHeader';
import VisitList from './components/List';
import EventBus from './eventBus';

export default {
  name: 'VisitIndex',
  components: {
    VisitListHeader,
    ScoreAverages,
    VisitListStats,
    VisitList,
    MobileFilterButton
  },
  mixins: [BackendPaginationMixin, WorkWithDashboardFilter, mobileScreenControl, DrilldownNavigationHelper],
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      currentScoreIndicator: null,
      searchQuery: '',
      reportStatus: [],
      highlightedScore: '',
      list: [],
      visitListIds: '',
      selectedScoreCategory: 'Overall',
      lastSort: null
    };
  },
  computed: {
    ...mapState(['Visit', 'GlobalFilters']),
    meta() {
      return this.Visit.list.meta;
    },
    stats() {
      return result(this.meta, 'aggregations.stats', {});
    },
    likeReportData() {
      const category = result(this.meta, 'aggregations.category_scores', [])
        .reduce((acc, item) => {
          acc[item.category] = item.scores;

          return acc;
        }, {});

      return {
        report: {
          scores: result(this.meta, 'aggregations.scores', {}),
          filtered_scores: {
            category
          }
        }
      };
    },
    fetchParams() {
      const { projectId, reportStatus, getQuery: rawQueries } = this;
      const { selectedFilterScopes, start_time: clientStartTime, end_time: clientEndTime } = this.GlobalFilters.query;
      const start_date_with_time = this.convertToServerTimeZone(`${clientStartTime} 00:00:00`);
      const end_date_with_time = this.convertToServerTimeZone(`${clientEndTime} 23:59:59`);
      const getQuery = JSON.parse(JSON.stringify(rawQueries));

      if (getQuery && getQuery.sort && this.selectedScoreCategory !== 'Overall' && getQuery.sort.search('scores') >= 0) {
        const scoreName = getQuery.sort.split('.')[1];

        getQuery.sort = `filtered_scores.${this.selectedScoreCategory}.${scoreName}`;
      }

      const paginationData = {
        ...getQuery,
        filter: {
          project: projectId,
          start_date_with_time,
          end_date_with_time,
          ...selectedFilterScopes
        }
      };

      if (!reportStatus.find(item => item === 'all') && reportStatus) {
        paginationData.report_statuses = reportStatus;
      }

      return { projectId, paginationData };
    }
  },
  watch: {
    reportStatus() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    selectedScoreCategory() {
      if ((this.getQuery.sort && this.getQuery.sort.search('scores') >= 0) || (this.lastSort && this.lastSort.search('scores') >= 0)) {
        this.pagination.currentPage = 1;
        this.fetchData();
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'visit-show') {
      sessionStorage.setItem('visitListFetchParams', JSON.stringify(this.fetchParams));
      sessionStorage.setItem('visitListMeta', JSON.stringify(this.meta));
      sessionStorage.setItem('storeTagList', JSON.stringify(this.GlobalFilters.filter_tags));
      sessionStorage.setItem('visitListIds', this.visitListIds);
    } else {
      sessionStorage.removeItem('visitListFetchParams');
      sessionStorage.removeItem('visitListMeta');
      sessionStorage.removeItem('storeTagList');
      sessionStorage.removeItem('visitListIds');
    }

    next();
  },
  beforeDestroy() {
    this.cleanVisitList();
  },
  mounted() {
    window.addEventListener('scroll', this.onScrollVisits);
    this.fetchData();
    EventBus.$on('SCORES_CHANGE', (item) => {
      this.currentScoreIndicator = item;
    });
  },
  activated() {
    window.addEventListener('scroll', this.onScrollVisits);
  },
  destroyed() {
    window.removeEventListener('scroll', this.onScrollVisits);
  },
  deactivated() {
    document.getElementById('project-navbar').classList.remove('sticky');

    window.removeEventListener('scroll', this.onScrollVisits);
  },
  methods: {
    ...mapActions(['fetchVisits', 'exportVisits', 'cleanVisitList']),
    onExport({ exportType, columns }) {
      const {
        projectId, getQuery, currentScoreIndicator, reportStatus
      } = this;
      const { selectedFilterScopes, start_time: clientStartTime, end_time: clientEndTime } = this.GlobalFilters.query;
      const start_date_with_time = this.convertToServerTimeZone(`${clientStartTime} 00:00:00`);
      const end_date_with_time = this.convertToServerTimeZone(`${clientEndTime} 23:59:59`);

      const query = {
        filter: {
          start_date_with_time,
          end_date_with_time,
          ...selectedFilterScopes
        },
        ...getQuery,
        ...(columns && {
          headers: columns
        })
      };

      if (reportStatus.length && !reportStatus.find(stat => stat === 'all')) {
        query.report_statuses = reportStatus;
      }

      if (currentScoreIndicator && currentScoreIndicator !== 'all') {
        query.category = currentScoreIndicator;
      }

      if (exportType === 'excel') {
        query.format = 'xlsx';
      } else {
        query.format = exportType;
      }

      this.exportVisits({ projectId, query });
    },
    async fetchData() {
      const { data: { data: newList } } = await this.fetchVisits(this.fetchParams);

      this.lastSort = this.getQuery.sort;

      if (this.pagination.currentPage > 1) {
        this.list = [...this.list, ...newList];
      } else {
        this.list = newList;
      }
    },
    handleReportStatusChange(status) {
      this.reportStatus = status;
    },
    onScrollVisits() {
      const projectNavbar = document.getElementById('project-navbar');
      const mobileFilterBtn = document.getElementsByClassName('filter-sidebar-icon')[0];
      const searchHeaderWrapper = document.getElementsByClassName('search-mobile')[0];
      const visitListReportScores = document.getElementsByClassName('visit-list-report-scores')[0];
      const sideTableBtns = document.getElementsByClassName('visit-button-group')[0];
      const mobileEditButton = document.getElementsByClassName('side-edit-column-btn')[0];

      const scrollHeight = this.screenWidth >= 461 ? 59 : 44;
      if (this.isMobile && window.top.scrollY >= scrollHeight) {
        projectNavbar.classList.add('sticky');
        mobileFilterBtn.classList.add('mobile-filter-visit-sticky');
        searchHeaderWrapper.classList.add('search-mobile-sticky');
        visitListReportScores.classList.add('report-scores-sticky-padding');

        if (window.top.scrollY >= 316) {
          sideTableBtns.classList.add('side-table-btn-position');
          mobileEditButton.classList.add('side-edit-column-btn-position');
          mobileFilterBtn.classList.add('with-buttons');
        } else {
          sideTableBtns.classList.remove('side-table-btn-position');
          mobileEditButton.classList.remove('side-edit-column-btn-position');
          mobileFilterBtn.classList.remove('with-buttons');
        }
      } else {
        projectNavbar.classList.remove('sticky');
        mobileFilterBtn.classList.remove('mobile-filter-visit-sticky');
        searchHeaderWrapper.classList.remove('search-mobile-sticky');
        visitListReportScores.classList.remove('report-scores-sticky-padding');
        sideTableBtns.classList.remove('side-table-btn-position');
        mobileEditButton.classList.remove('side-edit-column-btn-position');
      }
    },
    highlightScore(score, highlight = true) {
      if (highlight) {
        this.highlightedScore = score;

        return;
      }

      this.highlightedScore = '';
    },
    updateVisitListIds(list) {
      this.visitListIds = list;
    },
    selectScoreCategory(item) {
      this.selectedScoreCategory = item;
      EventBus.$emit('SCORES_CHANGE', item);
    }
  }
};
</script>

<style lang="scss">
.visit-container {
  position: relative;
  box-sizing: border-box;

  .visit-list-report-scores {
    margin-bottom: 20px;

    @include breakpoint(lg-max) {
      margin-top: 0;
      margin-bottom: 10px;
    }

    @include breakpoint(md-s-max) {
      margin-top: 10px;
      margin-bottom: 70px;
    }
  }

  .el-pagination button,
  .el-pagination span:not([class*=suffix]) {
    min-width: 20px;
  }

  .el-pagination .btn-next,
  .el-pagination .btn-prev {
    padding: 0 !important;
  }

  .list-meta-total {
    margin-left: 10px;
    font-size: 13px;
  }

  .el-main {
    padding: 10px 20px 20px 40px;
  }

  .searches {
    display: flex;
    justify-content: flex-end;
    padding: 0 20px;

    .el-input {
      width: auto;

      .el-input__inner {
        height: 34px;
      }
    }

    &.search-web {
      @include breakpoint(lg-max) {
        display: none;
      }
    }

    &.search-mobile {
      display: none;

      @include breakpoint(lg-max) {
        position: absolute;
        top: 10px;
        right: 10px;
        display: flex;
      }

      @include breakpoint(md-s-max) {
        position: initial;
        padding: 8px 10px;
        background-color: #fff;
      }
    }
  }

  .visit-list-header {
    display: flex;
    align-items: center;
    margin-right: 10px;
  }

  @include breakpoint(lg-max) {
    .el-header {
      display: none;
    }
  }

  @include breakpoint(md-s-max) {
    .mobilePadding {
      padding: 0 10px;
    }
  }

  @include breakpoint(sm-max) {
    .module-container__header {
      height: 48px !important;
    }
  }
}

.tab-header-sticky-position {
  position: fixed !important;
  top: 40px !important;
  z-index: 8 !important;
  width: 100%;
}

.search-mobile-sticky {
  position: fixed !important;
  top: 46px !important;
  right: 0 !important;
  z-index: 8 !important;

  @include breakpoint(md-s-max) {
    top: 38px !important;
  }
}

.mobile-filter-visit-sticky {
  position: fixed !important;
  top: 40px !important;
  left: 0 !important;
  z-index: 8 !important;
  width: 100%;
  padding: 6px 8px;
  background: white;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);

  &.with-buttons {
    @include breakpoint(md-s-max) {
      padding: 6px 8px 54px 8px;
    }
  }
}

.report-scores-sticky-padding {
  padding-top: 84px;
}

.side-edit-column-btn-position {
  position: fixed !important;
  top: 96px !important;
  z-index: 9 !important;
}

.side-table-btn-position {
  position: fixed !important;
  top: 196px !important;
  z-index: 9 !important;

  @include breakpoint(md-s-max) {
    top: 97px !important;
  }
}
</style>
